"use client"

import styled from "@emotion/styled"
import { Button, Divider, TextField, Typography } from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { postTrafficUpdateAll } from "api"
import debounce from "debounce"
import { useTranslations } from "next-intl"
import dynamic from "next/dynamic"
import { useCallback, useState } from "react"
import { useInView } from "react-intersection-observer"
import { TransitionUp, useInViewConfig } from "ui"
import Container from "ui/Container"
import CloseXIcon from "ui/icons/CloseXIcon"
import Search from "ui/icons/Search"
import { sharedColors } from "ui/theme/colors"
import { mediaQueries } from "ui/theme/mediaQueries"
import { delayTime } from "ui/transitions/utils"
import { extractProperty, getNonUmbracoUrl, Link, TrafficUpdate } from "utils"
import { transformApiToTrafficUpdate } from "utils/transformUpdate"
import cactusAnimations from "./cactus.json"
import lottieAnimation from "./lottie.json"
import TraffixBox from "./TraffixBox"

const Lottie = dynamic(() => import("lottie-react"), { ssr: false })

const OuterContainer = styled.div`
  width: 100%;
  background-color: ${sharedColors.mediumGrayLight};
  display: flex;
  flex-direction: column;
`

const StyledContainer = styled.div`
  position: relative;
  width: 100%;
`

const InnerContainer = styled.div<{ $isData: boolean }>`
  padding: 40px 16px 24px 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    box-sizing: content-box;
    padding: 40px 16px;
    height: 324px;
    padding: 64px 0;
    ${(props) => props.$isData && "padding-bottom: 0;"}
  }

  gap: 24px;
  display: flex;
  flex-direction: column;
  /* align-items: center; */
  /* justify-content: center; */
  ${({ theme }) => theme.breakpoints.down("md")} {
  }
`

const InnerInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 70px;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  ${({ theme }) => theme.breakpoints.up("md")} {
    justify-content: unset;
  }
  gap: 16px;
  align-items: center;
`

const StyledDivider = styled(Divider)`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
  }
  height: 23px;
  background-color: ${sharedColors.labelGray};
`

const StyledButton = styled(Button)`
  padding: 0 !important;
  height: fit-content !important;
  border-radius: 0 !important;
  font-weight: 400 !important;
  text-decoration: underline;
  &:hover {
    box-shadow: inset 0 -1px 0 ${sharedColors.black};
    background-color: transparent !important;
  }
  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const StyledMoreButton = styled(Button)`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 280px;
  }
`
const StyledLottie = styled(Lottie)`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
  }
  transform: rotateY(180deg);
  position: absolute;
  bottom: 0;
  right: 0;
`

const StyledTextField = styled(TextField)`
  width: 100%;
  margin-top: 20px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 400px;
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    width: 500px;
  }
  svg[aria-label="close"] {
    cursor: pointer;
  }
  label {
    font-size: 16px;
    color: ${sharedColors.black};

    top: 0px;
  }
  label[data-shrink="true"] {
    transform: translate(10px, -35px);
  }
  input {
    padding-top: 16.5px;
    padding-bottom: 16.5px;
  }
  input:-webkit-autofill {
    -webkit-text-fill-color: black; /* Surcharge la font color d'autofill */
    -webkit-background-clip: text; /* Supprime le background autofill, utile pour le border radius */
    box-shadow: none !important;
  }
`

const DataContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.down("md")} {
    overflow: hidden;
  }

  ${({ theme }) => theme.breakpoints.up("md")} {
    display: grid;
    width: 100%;
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const APIContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 16px;
  padding-bottom: 40px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 0;
    padding-bottom: 64px;
  }
`

const StyledForm = styled.form`
  height: fit-content;
  width: 100%;
  ${mediaQueries.mobile} {
    padding-bottom: 16px;
  }
`

const StyledTypography = styled(Typography)`
  ${({ theme }) => theme.breakpoints.up("md")} {
    /* margin-top: 45px; */
  }
`

type UndStr = string | undefined

export default function TrafficUpdates({
  data,
  trafficUpdates,
}: {
  data: any
  trafficUpdates: TrafficUpdate[]
}) {
  const t = useTranslations("Homepage")
  const [inputValue, setInputValue] = useState("")
  const title = extractProperty(data, "title") as UndStr
  const linkText = extractProperty(data, "link")?.[0]?.name as UndStr
  const linkUrl = extractProperty(data, "link")?.[0]?.url as UndStr
  const subtitle = extractProperty(data, "subtitle") as UndStr
  const buttonText = extractProperty(data, "button")?.[0]?.name as UndStr
  const buttonUrl = extractProperty(data, "button")?.[0]?.url as UndStr
  const tInner = useTranslations("InnerPages")
  const { ref, inView } = useInView(useInViewConfig)
  const pageSize = 10
  const requestPage = 10

  const {
    mutate,
    data: apiData,
    reset,
  } = useMutation({
    mutationFn: async (lineOrStopId: string) => {
      const res = await postTrafficUpdateAll({
        pageSize,
        requestPage,
        searchTerm: lineOrStopId,
        isHomePage: true,
      })
      const apiItems = res.data.body.trafficUpdates.items.map((el) =>
        transformApiToTrafficUpdate(el),
      )
      const umbracoItems = trafficUpdates.filter((el) =>
        el.lines.some((line) => line.toString() === lineOrStopId),
      )
      const items = [...umbracoItems, ...apiItems]
      const sortedItems = items.sort((a, b) => {
        const dateA = new Date(a.activePeriodStart)
        const dateB = new Date(b.activePeriodStart)
        return dateB.getTime() - dateA.getTime()
      })
      return sortedItems
    },
  })

  const handleDebouncedChange = useCallback(
    debounce((value: string) => {
      if (value) mutate(value)
    }, 1000),
    [mutate],
  )

  const showCactus = apiData?.length === 0 && !!inputValue

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value) // Update UI immediately
    handleDebouncedChange(value) // Debounce just the search
  }

  const handleClear = () => {
    setInputValue("")
    reset()
  }

  return (
    <OuterContainer>
      <Container>
        <StyledContainer ref={ref}>
          <TransitionUp $inView={inView} $delay={delayTime}>
            {showCactus ? (
              <StyledLottie animationData={cactusAnimations} />
            ) : (
              <StyledLottie animationData={lottieAnimation} />
            )}
            <InnerContainer $isData={!!data}>
              <InnerInner>
                <Header>
                  <Typography variant="h2">{title}</Typography>
                  <StyledDivider orientation="vertical" />
                  <StyledButton
                    variant="text"
                    disableRipple
                    href={getNonUmbracoUrl(linkUrl)}
                    LinkComponent={Link}
                  >
                    {linkText}
                  </StyledButton>
                </Header>
                <Typography variant="body1">{subtitle}</Typography>
                <StyledTextField
                  name="search"
                  label={tInner("enterLine")}
                  variant="filled"
                  slotProps={{
                    input: {
                      endAdornment: inputValue ? (
                        <CloseXIcon onClick={handleClear} />
                      ) : (
                        <Search />
                      ),
                    },
                  }}
                  value={inputValue}
                  onChange={handleInputChange}
                />

                {!!apiData?.length && (
                  <StyledTypography variant="body2">
                    {(() => {
                      const translated = tInner("weFound20Updat")
                      const parts = translated.split("20")
                      return (
                        <>
                          {parts[0]}
                          <span style={{ fontWeight: 700 }}>
                            {apiData.length}
                          </span>
                          {parts[1]}
                        </>
                      )
                    })()}
                  </StyledTypography>
                )}
                {showCactus ? (
                  <StyledTypography variant="body2">
                    {t("noUpdatesFoundT")}
                  </StyledTypography>
                ) : null}
              </InnerInner>
            </InnerContainer>
          </TransitionUp>
        </StyledContainer>
      </Container>
      {!!apiData?.length && (
        <Container>
          <APIContainer>
            <DataContainer>
              {apiData.slice(0, 3).map((el, i) => (
                <TraffixBox
                  key={i}
                  el={el}
                  overflow={true}
                  spaceBetween={true}
                />
              ))}
            </DataContainer>
            <StyledMoreButton
              variant="primary"
              href={getNonUmbracoUrl(buttonUrl) + "?search=" + inputValue}
              LinkComponent={Link}
            >
              {buttonText}
            </StyledMoreButton>
          </APIContainer>
        </Container>
      )}
    </OuterContainer>
  )
}
